// ============================================================
// ClientSourceSection.tsx
// Referral / lead-origin section. Sits between Automatic and Client
// Details in the sequential flow.
//
// Redesign: no dedicated mockup exists for this section, so it follows the
// established People-section pattern (Client details / Witnesses / the
// Stage-3 party sections): the shared SectionCards view/edit cards, the
// at-a-glance strip and the shared footer. It inherits card presence,
// instant Save (flush + save-fail message), Cancel-restore and Edit all.
//
// DATA LOGIC IS UNCHANGED from the old long form (@ 93bd7df):
//   • every input writes ONE field of currentCase.clientSource via
//     updateCase({ clientSource: { ...live, [field]: value } }) → the
//     debounced case autosave; same fieldKeys (presence + live sync)
//   • AddressLookup writes all 5 address fields in ONE updateCase +
//     broadcastFieldEdits(CLIENT_SOURCE_ADDRESS_FIELD_KEYS)
//   • postcode uppercased
//   • N/A ticked → all fields cleared in ONE updateCase + notApplicable
//     true + broadcastFieldEdits(all keys); unticked → notApplicable false.
//     (`notApplicable` has no fieldKey — reconciles on the next edit, as
//     before.) NEW: when there is data to clear, a confirm guards it.
//   • validateClientSource gates Mark complete (none when N/A)
//   • no rich text / live documents in this section
// ============================================================
import React, { useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { ClientSource, Address } from '../../types/case.types'
import FormField from '../shared/FormField'
import PhoneLink, { PHONE_GLANCE_LINK } from '../shared/PhoneLink'
import FieldError from '../shared/FieldError'
import AddressLookup from '../shared/AddressLookup'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import { validateClientSource } from '../../utils/validation'
import { broadcastFieldEdits } from '../../hooks/usePresence'
import { flushAutoSave } from '../../hooks/useAutoSave'
import { inpCls, FGRID } from '../clientInfo/clientInfoRegistry'
import { CARD, BTN_SEC, SM } from '../clientInfo/ClientInfoSections'
import SectionCards, { type CardDef } from '../case/sections/SectionCards'
import {
  KIcon, Glance, GlanceDash, SectionFooter, Kv, Empty, AddressBlock,
  DL, BIG, MONO, CHIP_N, LINK,
} from '../case/sections/sectionKit'

// Live edit-sync: the address-lookup and the N/A toggle write several
// clientSource fields programmatically (no per-field blur), so they broadcast
// the affected fieldKeys explicitly.
const CLIENT_SOURCE_ADDRESS_FIELD_KEYS = ['clientSource.address-line-1', 'clientSource.address-line-2', 'clientSource.town', 'clientSource.county', 'clientSource.postcode']
const CLIENT_SOURCE_ALL_FIELD_KEYS = ['clientSource.client-source-name', 'clientSource.contact-number', 'clientSource.email-address', ...CLIENT_SOURCE_ADDRESS_FIELD_KEYS]

// Field → existing fieldKey (presence, live sync, Cancel-restore broadcast).
const FIELD_KEY: Partial<Record<keyof ClientSource, string>> = {
  name: 'clientSource.client-source-name',
  contactNumber: 'clientSource.contact-number',
  emailAddress: 'clientSource.email-address',
  addressLine1: 'clientSource.address-line-1',
  addressLine2: 'clientSource.address-line-2',
  addressTown: 'clientSource.town',
  addressCounty: 'clientSource.county',
  addressPostcode: 'clientSource.postcode',
}
// Field → validateClientSource error key.
const ERR_KEY: Partial<Record<keyof ClientSource, string>> = {
  name: 'clientSourceName',
  contactNumber: 'clientSourceContactNumber',
  emailAddress: 'clientSourceEmailAddress',
  addressPostcode: 'clientSourceAddressPostcode',
}
const DATA_FIELDS: (keyof ClientSource)[] = ['name', 'contactNumber', 'emailAddress', 'addressLine1', 'addressLine2', 'addressTown', 'addressCounty', 'addressPostcode']

const ClientSourceSection: React.FC = () => {
  const { currentCase, updateCase } = useCasevoStore()
  const [confirmNa, setConfirmNa] = useState(false)
  if (!currentCase) return null

  const cs = currentCase.clientSource
  const errors = validateClientSource(cs)
  const na = cs.notApplicable

  // Spread the LIVE store object (same as Accident/Injury) so a write never
  // reverts a field changed meanwhile by a remote edit.
  const liveCs = (): ClientSource => useCasevoStore.getState().currentCase?.clientSource ?? cs
  const update = (field: keyof ClientSource, value: unknown): void => {
    updateCase({ clientSource: { ...liveCs(), [field]: value } })
  }
  const updateMany = (changes: Partial<ClientSource>): void => {
    updateCase({ clientSource: { ...liveCs(), ...changes } })
  }

  // Tick N/A → clear all fields and set notApplicable (one write), exactly as
  // the old checkbox handler did.
  const applyNa = (): void => {
    updateCase({
      clientSource: {
        name: '',
        contactNumber: '',
        emailAddress: '',
        addressLine1: '',
        addressLine2: '',
        addressTown: '',
        addressCounty: '',
        addressPostcode: '',
        notApplicable: true,
      },
    })
    // Programmatic multi-field clear — broadcast the cleared fields for sync.
    broadcastFieldEdits(CLIENT_SOURCE_ALL_FIELD_KEYS)
  }
  const hasData = DATA_FIELDS.some((f) => String(cs[f] ?? '').trim() !== '')
  const markNa = (): void => { if (hasData) setConfirmNa(true); else applyNa() }
  const unmarkNa = (): void => update('notApplicable', false)

  const text = (
    ctx: { idPrefix: string; err: (k: string) => string | undefined },
    f: keyof ClientSource, label: string,
    opts: { type?: string; placeholder?: string; full?: boolean; upper?: boolean; mono?: boolean } = {},
  ): React.ReactNode => {
    const errKey = ERR_KEY[f]
    const err = errKey ? ctx.err(errKey) : undefined
    const id = `${ctx.idPrefix}-${String(f)}`
    return (
      <div className={opts.full ? 'sm:col-span-2' : ''}>
        <FormField variant="info" label={label} fieldKey={FIELD_KEY[f]} htmlFor={id} required={!!errKey}>
          <input
            id={id}
            type={opts.type ?? 'text'}
            value={String(cs[f] ?? '')}
            onChange={(e) => update(f, opts.upper ? e.target.value.toUpperCase() : e.target.value)}
            placeholder={opts.placeholder}
            aria-invalid={err ? true : undefined}
            className={`${inpCls(!!err)} ${opts.mono ? 'font-mono tracking-[0.04em]' : ''}`}
          />
          <FieldError message={err} />
        </FormField>
      </div>
    )
  }

  const addrLines = [cs.addressLine1, cs.addressLine2, [cs.addressTown, cs.addressCounty].filter((s) => s.trim()).join(', ')]
    .map((s) => s.trim()).filter(Boolean)
  const pc = cs.addressPostcode.trim()
  const addressValue: Address | null =
    cs.addressLine1 || cs.addressLine2 || cs.addressTown || cs.addressCounty || cs.addressPostcode
      ? { line1: cs.addressLine1, line2: cs.addressLine2, town: cs.addressTown, county: cs.addressCounty, postcode: cs.addressPostcode }
      : null

  const defs: CardDef<ClientSource>[] = na ? [] : [
    {
      key: 'contact', title: 'Client source', sub: 'Who referred the client, and how to reach them', icon: <KIcon name="users" size={17} />,
      fields: ['name', 'contactNumber', 'emailAddress'],
      errorKeys: ['clientSourceName', 'clientSourceContactNumber', 'clientSourceEmailAddress'],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="Client source name" full>{cs.name.trim() ? <span className={BIG}>{cs.name}</span> : <Empty onAdd={ctx.onAdd} />}</Kv>
          <Kv label="Contact number">{cs.contactNumber.trim() ? <PhoneLink value={cs.contactNumber} className={LINK} /> : <Empty onAdd={ctx.onAdd} />}</Kv>
          <Kv label="Email address">{cs.emailAddress.trim() ? <a className={`${LINK} [overflow-wrap:anywhere]`} href={`mailto:${cs.emailAddress.trim()}`}>{cs.emailAddress}</a> : <Empty onAdd={ctx.onAdd} />}</Kv>
        </dl>
      ),
      edit: (ctx) => (
        <div className={FGRID}>
          {text(ctx, 'name', 'Client source name', { full: true })}
          {text(ctx, 'contactNumber', 'Contact number', { type: 'tel', placeholder: '07700 900000' })}
          {text(ctx, 'emailAddress', 'Email address', { type: 'email', placeholder: 'name@example.com' })}
        </div>
      ),
    },
    {
      key: 'address', title: 'Address', sub: 'Postal address · postcode required', icon: <KIcon name="map" size={17} />,
      fields: ['addressLine1', 'addressLine2', 'addressTown', 'addressCounty', 'addressPostcode'],
      errorKeys: ['clientSourceAddressPostcode'],
      presenceKeys: [...CLIENT_SOURCE_ADDRESS_FIELD_KEYS, 'clientSource.address-search'],
      view: (ctx) => (
        <dl className={DL}>
          <Kv label="Address" full>{addrLines.length || pc ? <AddressBlock lines={addrLines} postcode={pc || undefined} /> : <Empty onAdd={ctx.onAdd} />}</Kv>
        </dl>
      ),
      edit: (ctx) => (
        <div className={FGRID}>
          <div className="sm:col-span-2">
            <FormField variant="info" label="Search address" fieldKey="clientSource.address-search" htmlFor={`${ctx.idPrefix}-search`}>
              {/* Picks a suggestion and writes all 5 fields in ONE updateCase. */}
              <AddressLookup
                variant="info"
                inputId={`${ctx.idPrefix}-search`}
                value={addressValue}
                onSelect={(addr) => {
                  updateMany({ addressLine1: addr.line1, addressLine2: addr.line2, addressTown: addr.town, addressCounty: addr.county, addressPostcode: addr.postcode })
                  broadcastFieldEdits(CLIENT_SOURCE_ADDRESS_FIELD_KEYS)
                }}
                onClear={() => {
                  updateMany({ addressLine1: '', addressLine2: '', addressTown: '', addressCounty: '', addressPostcode: '' })
                  broadcastFieldEdits(CLIENT_SOURCE_ADDRESS_FIELD_KEYS)
                }}
              />
            </FormField>
          </div>
          {text(ctx, 'addressLine1', 'Address line 1')}
          {text(ctx, 'addressLine2', 'Address line 2')}
          {text(ctx, 'addressTown', 'Town')}
          {text(ctx, 'addressCounty', 'County')}
          {text(ctx, 'addressPostcode', 'Postcode', { placeholder: 'LS6 3AB', upper: true, mono: true })}
        </div>
      ),
    },
  ]

  const naSub = 'Not applicable'
  const glance = (
    <Glance cells={[
      { icon: 'users', label: 'Source', value: na ? 'Not applicable' : (cs.name.trim() || <GlanceDash />), sub: na ? 'Came to the firm directly' : 'Client source name' },
      { icon: 'phone', label: 'Phone', value: !na && cs.contactNumber.trim() ? <PhoneLink value={cs.contactNumber} className={PHONE_GLANCE_LINK} size="sm" /> : <GlanceDash />, sub: na ? naSub : cs.contactNumber.trim() ? 'Contact number' : 'Not recorded' },
      { icon: 'mail', label: 'Email', value: !na && cs.emailAddress.trim() ? cs.emailAddress : <GlanceDash />, sub: na ? naSub : cs.emailAddress.trim() ? 'Email address' : 'Not recorded' },
      {
        icon: 'map', label: 'Based in',
        value: !na && (cs.addressTown.trim() || pc) ? <>{cs.addressTown}{cs.addressTown.trim() && pc ? ' · ' : ''}{pc && <span className={`${MONO} !text-[13px]`}>{pc}</span>}</> : <GlanceDash />,
        sub: na ? naSub : (cs.addressLine1.trim() || 'Address'),
      },
    ]} />
  )

  // N/A control (replaces the old top-of-form checkbox).
  const notice = (
    <div className={`${CARD} flex items-center gap-3 flex-wrap mb-[14px] py-[11px] pr-[14px] pl-[18px]`}>
      <span className="w-8 h-8 rounded-[9px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] grid place-items-center shrink-0"><KIcon name="flag" size={16} /></span>
      <div className="min-w-0 flex-1 text-[13.5px] text-casevo-forest">
        {na ? (
          <>
            <span className="flex items-center gap-2 flex-wrap">No client source <span className={CHIP_N}>Not applicable</span></span>
            <span className="block text-[12px] text-casevo-text-soft">The client came to the firm directly, so nothing is required here.</span>
          </>
        ) : (
          <>
            Did the client come to the firm directly?
            <span className="block text-[12px] text-casevo-text-soft">Mark this section not applicable if nobody referred them.</span>
          </>
        )}
      </div>
      {na ? (
        <button type="button" className={`${BTN_SEC} ${SM}`} onClick={unmarkNa}><KIcon name="plus" size={13} />Record a client source</button>
      ) : (
        <button type="button" className={`${BTN_SEC} ${SM}`} onClick={markNa}>Mark not applicable</button>
      )}
    </div>
  )

  return (
    <>
      <SectionCards<ClientSource>
        // Remount on the N/A flip so no open card / Cancel snapshot survives it
        // (a stale snapshot's Cancel would resurrect the cleared values).
        key={na ? 'na' : 'source'}
        value={cs}
        onChange={updateMany}
        fieldKeyOf={(f) => FIELD_KEY[f]}
        onBroadcast={broadcastFieldEdits}
        onFlush={flushAutoSave}
        errors={errors}
        defs={defs}
        title="Client source"
        sub="Who referred the client to the firm."
        glance={glance}
        notice={notice}
        hideEditAll={na}
        footer={<SectionFooter sectionKey="clientSource" errors={errors} readySub={na ? 'Marked not applicable: no fields are required.' : undefined} />}
        idPrefix={`cs-${currentCase.id}`}
      />
      <ConfirmDialogue
        isOpen={confirmNa}
        title="Mark client source not applicable?"
        message="This clears the client source name, contact details and address recorded for this case."
        confirmLabel="Clear and mark N/A"
        onConfirm={() => { setConfirmNa(false); applyNa() }}
        onCancel={() => setConfirmNa(false)}
      />
    </>
  )
}
export default ClientSourceSection
